/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The v2 header's New menu: "New lead", "New contact"... Each entry opens the
 * list page's own create dialog through ?create=1 (hooks/useOpenCreateFromUrl),
 * so it creates exactly what the page's Add button creates.
 *
 * An entry shows only when the user can open the page (it is among their
 * allowed routes, so the module and route gating already ran) and holds
 * create on the object the server checks, with the same extra conditions the
 * page puts on its own button. The Deals list page has no create (its
 * dialog is never opened), so there is no "New deal". Travel bookings come
 * from /travel-orders/new, listed separately by AppFrame.
 */

export const CREATE_ACTIONS = [
  { key: "leads", noun: "lead", object: "leads" },
  { key: "contacts", noun: "contact", object: "contacts" },
  { key: "accounts", noun: "account", object: "accounts" },
  { key: "cases", noun: "case", object: "cases" },
  { key: "tasks", noun: "task", object: "activities" },
  { key: "quotation", noun: "quotation", object: "quotations" },
  { key: "orders", noun: "order", object: "orders" },
  // Manual invoices are a Travel feature on the Invoices page.
  { key: "invoices", noun: "invoice", object: "invoices", travelOnly: true },
];

/** "Enquiries" -> "enquiry"; labels that are not one plural word keep the default noun. */
export function nounFor(action, app) {
  const label = app?.labels?.[action.key];
  if (!label || /\s/.test(label)) return action.noun;
  const lower = label.toLowerCase();
  if (lower.endsWith("ies")) return `${lower.slice(0, -3)}y`;
  if (lower.endsWith("s")) return lower.slice(0, -1);
  return lower;
}

/**
 * @param routes      allowed route objects ({ key, route }), nested `collapse` included
 * @param canCreate   (objectApiName) => boolean
 * @param showTravel  the company travel switch
 * @param app         active app (labels), or null
 */
export function availableCreateActions({ routes, canCreate, showTravel, app }) {
  const byKey = new Map();
  const walk = (list) =>
    (list || []).forEach((r) => {
      if (r?.key && r.route && !byKey.has(r.key)) byKey.set(r.key, r.route);
      if (Array.isArray(r?.collapse)) walk(r.collapse);
    });
  walk(routes);
  return CREATE_ACTIONS.filter(
    (a) => byKey.has(a.key) && canCreate(a.object) && (!a.travelOnly || showTravel)
  ).map((a) => {
    const noun = nounFor(a, app);
    return { key: `create-${a.key}`, name: `New ${noun}`, route: `${byKey.get(a.key)}?create=1` };
  });
}
